import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DocumentModel, DocumentStub, DocumentProjectAggregate } from '../models/document-model';
import { CheckCheckStageAggregate, CheckStub } from '../models/checks-model';
import { CommentDTO, CommentRoleRequest } from '../models/comment-model';
import { API_ENDPOINTS } from '../common/api/api-endpoints';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private readonly http = inject(HttpClient);

  // Data Stream (lokale Kopie für Mock-Betrieb)
  private documents$ = new BehaviorSubject<DocumentModel[]>([]);

  constructor() {
    if (environment.useMocks) {
      this.loadDocuments();
    }
  }

  // Load initial data (Mock-Betrieb only)
  private loadDocuments(): void {
    this.http.get<DocumentModel[]>(API_ENDPOINTS.DOCUMENTS)
      .pipe(tap(docs => this.documents$.next(docs)))
      .subscribe();
  }

  // Read Operations
  getDocuments(): Observable<DocumentModel[]> {
    return this.documents$.asObservable();
  }

  // CRUD Operations (Mock-Betrieb)
  createDocument(document: Partial<DocumentModel>): void {
    const newDocument: DocumentModel = {
      guid: crypto.randomUUID(),
      uri: document.uri ?? '',
      displayName: document.displayName ?? '',
    };
    this.documents$.next([...this.documents$.value, newDocument]);
  }

  updateDocument(guid: string, patch: Partial<DocumentModel>): void {
    this.documents$.next(
      this.documents$.value.map(doc =>
        doc.guid === guid ? { ...doc, ...patch } : doc
      )
    );
  }

  deleteDocument(guid: string): void {
    this.documents$.next(
      this.documents$.value.filter(doc => doc.guid !== guid)
    );
  }

  /**
   * Fügt ein Dokument lokal hinzu.
   * Gibt die GUID zurück, oder null bei Duplikat.
   */
  addDocumentToCheck(fileName: string, _checkGuid: string, _checkStatus: number): string | null {
    const existingDoc = this.documents$.value.find(d => d.displayName === fileName);
    if (existingDoc) return null; // Duplikat

    const guid = crypto.randomUUID();
    this.documents$.next([...this.documents$.value, { guid, uri: fileName, displayName: fileName }]);
    return guid;
  }

  /**
   * Entfernt ein Dokument lokal (Mock-Betrieb).
   */
  unlinkDocumentFromCheck(_checkGuid: string, documentGuid: string, _checkStatus: number): void {
    this.deleteDocument(documentGuid);
  }

  // ===== Backend API Methoden =====

  /** GET /document/{guid} */
  getDocumentByIdApi(guid: string): Observable<DocumentProjectAggregate> {
    return this.http.get<DocumentProjectAggregate>(`${API_ENDPOINTS.DOCUMENT}/${guid}`);
  }

  /** PUT /document/{guid} → 202 */
  updateDocumentApi(guid: string, payload: DocumentStub): Observable<DocumentModel> {
    return this.http.put<DocumentModel>(`${API_ENDPOINTS.DOCUMENT}/${guid}`, payload);
  }


  /** GET /document/{documentGuid}/check */
  getChecksByDocumentApi(documentGuid: string): Observable<CheckCheckStageAggregate[]> {
    return this.http.get<CheckCheckStageAggregate[]>(`${API_ENDPOINTS.DOCUMENT}/${documentGuid}/check`);
  }

  /** POST /document/{documentGuid}/check → 201 */
  createCheckForDocumentApi(documentGuid: string, payload: CheckStub): Observable<CheckCheckStageAggregate> {
    return this.http.post<CheckCheckStageAggregate>(`${API_ENDPOINTS.DOCUMENT}/${documentGuid}/check`, payload);
  }

  /** GET /document/{documentId}/comment → CommentDTO[] */
  getDocumentCommentsApi(documentId: string): Observable<CommentDTO[]> {
    return this.http.get<CommentDTO[]>(`${API_ENDPOINTS.DOCUMENT}/${documentId}/comment`);
  }

  /**
   * POST /document/{documentId}/comment/{commentId} → 201
   * Verknüpft einen bestehenden Check-Kommentar mit einem Dokument.
   */
  linkCommentToDocumentApi(documentId: string, commentId: string, body: CommentRoleRequest): Observable<CommentDTO> {
    return this.http.post<CommentDTO>(`${API_ENDPOINTS.DOCUMENT}/${documentId}/comment/${commentId}`, body);
  }

  /** DELETE /document/{documentGuid} → 204 */
  softDeleteDocument(documentGuid: string): Observable<void> {
    return this.http.delete<void>(`${API_ENDPOINTS.DOCUMENT}/${documentGuid}`);
  }

  // Computed Helper
  createDocumentsById(documents: DocumentModel[]): Map<string, DocumentModel> {
    return new Map(documents.map(doc => [doc.guid, doc]));
  }
}
